import React, { useMemo, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { ActivationContent } from "./Content";
import { trackClickHelp, trackClickEmail } from "@companion-core/shared/app/tracking/register";
import "@companion-core/web/src/Assets/Styles/scss/register.scss";
import { useActivation } from "@companion-core/shared/app/Components/SignUp/hooks/useActivation";
import { useTrackingStore } from "@companion-core/shared/app/Store/trackingStore";
import { getModule } from "@companion-core/shared/app/Utils/config";

export const Activation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vToken = useMemo(() => searchParams.get("vtoken"), [searchParams]);
  const {
    t,
    activationContent,
    activationErrorCode,
    onSubmitActivation,
    onSubmitIdentity,
    setActivationContent,
    memberData,
  } = useActivation(vToken, "website");

  useEffect(() => {
    if (!vToken) {
      navigate("/register/member");
    }
  }, [vToken]);

  const ActivationHeader = () => {
    return (
      ["activation", "activation-failed"].includes(activationContent) && (
        <div data-testid="register__header" className="register__header">
          <span
            data-testid="register__header--back-button"
            className="register__header-back-button cursor-pointer"
            onClick={() => setActivationContent("identity")}>
            <FontIcon name="JP-Arrow-left-bold" size={24} />
            {t("register.back-button.label")}
          </span>
        </div>
      )
    );
  };

  const redirectCallbackMap: Record<
    string,
    (param?: Record<string, unknown>) => void | Promise<void>
  > = {
    verified: () => setActivationContent("identity"),
    "verify-expired": () => navigate("/register/member"),
    identity: (identityFormData?: Record<string, unknown>) => onSubmitIdentity(identityFormData),
    activation: (activationFormData?: Record<string, unknown>) =>
      onSubmitActivation(activationFormData),
    "successful-activation": () => navigate("/login"),
    "activation-failed": () => setActivationContent("identity"),
  };

  const ActivationFooter = () => {
    const { currentPage } = useTrackingStore((state) => ({
      currentPage: state.currentPage,
    }));
    const identityFooter = useMemo(
      () => getModule("register")?.modules?.activation?.identityFooter ?? "",
      [],
    );
    const isIdentity = useMemo(() => activationContent === "identity", [activationContent]);
    const hasEmail = useMemo(
      () => identityFooter?.displayEmail && !!identityFooter?.email,
      [identityFooter],
    );
    const NeedHelpLink = () => (
      <Link
        data-testid="register__need-help--link"
        to="/need-help"
        className="register__need-help-link body-text-7"
        onClick={() => trackClickHelp(currentPage)}>
        {t("register.links.help.link")}
      </Link>
    );
    return (
      <div data-testid="register__need-help" className="register__need-help activation__need-help">
        <span className="body-text-8">
          {isIdentity ? (
            hasEmail ? (
              <>
                {t("register.activation.identity.footer.email.pre")}{" "}
                {t("register.activation.identity.footer.email.post")}
                <a
                  data-testid="register__contact--email"
                  href={`mailto:${identityFooter?.email}`}
                  className="body-text-7"
                  onClick={() => trackClickEmail(currentPage)}>
                  {identityFooter?.email}
                </a>
              </>
            ) : (
              <>
                {t("register.activation.identity.links.help.pre")}{" "}
                {t("register.activation.identity.links.help.post")}
                <NeedHelpLink />
              </>
            )
          ) : (
            <>
              {t("register.links.help.pre")} {t("register.links.help.post")}
              <NeedHelpLink />
            </>
          )}
        </span>
      </div>
    );
  };

  return (
    <div data-testid="activation" className="register">
      <ActivationHeader />
      <div className="register__container">
        <ActivationContent
          state={activationContent}
          redirectCallback={redirectCallbackMap[activationContent]}
          memberData={memberData}
          activationErrorCode={activationErrorCode}
        />
        <ActivationFooter />
      </div>
    </div>
  );
};
